import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { CoreData, NameEntry, SexIdx } from "../data/types";
import { useStore } from "../state/store";
import { decisions, haptic, ratings, recordDuel, recordTie, settings } from "../state/app";
import { closeOverlay } from "../state/router";
import { genderClass } from "../data/format";
import { entryFor } from "../data/load";
import { inGroup, standing } from "../state/nameoff";
import { Sheet } from "./Sheet";
import { Segmented } from "./FilterSheet";
import { nameSizeClass } from "./NameCard";
import { chosen } from "../state/chosen";
import { pickName } from "./Chosen";

export function NameOffSheet({ core, open }: { core: CoreData; open: boolean }) {
  const map = useStore(decisions, (s) => s.map);
  const pairs = useStore(ratings, (s) => s.pairs);
  const chosenKey = useStore(chosen, (s) => s.key);
  const pref = useStore(settings, (s) => s.sex);

  // choosing for either: girls' and boys' names have a Name-off each
  const liked = useMemo(
    () =>
      Object.keys(map)
        .filter((k) => map[k].d > 0)
        // oldest first: a name liked later is fitted in with a few picks, not a fresh start
        .sort((a, b) => map[a].t - map[b].t),
    [map],
  );
  const counts = useMemo(() => {
    const c = [0, 0];
    for (const k of liked) {
      const e = core.byKey.get(k);
      if (inGroup(e, 0)) c[0]++;
      if (inGroup(e, 1)) c[1]++;
    }
    return c;
  }, [liked, core]);
  const both = pref === "X" && counts[0] >= 2 && counts[1] >= 2;
  const [chosenGroup, setGroup] = useState<SexIdx>(counts[1] > counts[0] ? 1 : 0);
  const group: SexIdx | null = both ? chosenGroup : null;

  const keys = useMemo(() => {
    const inIt = liked.filter((k) => inGroup(core.byKey.get(k), group));
    const loved = inIt.filter((k) => map[k].d === 2);
    // with enough loved names, the contest is between those
    return loved.length >= 6 ? loved : inIt;
  }, [liked, group, map, core]);
  const st = useMemo(() => standing(keys, pairs), [keys, pairs]);
  const name = (k: string): NameEntry => entryFor(core, k, map[k]?.n);

  // each pick brings a new pair: the keyboard's place moves to it, and a screen reader hears it
  const first = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  const [said, setSaid] = useState("");
  const ask = st.ask;
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    first.current?.focus();
  }, [ask?.[0], ask?.[1]]);

  const pick = (win: string, lose: string) => {
    refocus.current = !!document.activeElement?.closest(".duel");
    recordDuel(win, lose);
    haptic(10);
    setSaid(`Picked ${name(win).name}.`);
  };
  const skip = () => {
    if (!ask) return;
    refocus.current = true;
    recordTie(ask[0], ask[1]);
    setSaid("Skipped.");
  };
  const again = () => {
    // forget this contest's answers: the names, and the rest of the shortlist, stay
    const set = new Set(keys);
    ratings.set((s) => ({
      pairs: Object.fromEntries(Object.entries(s.pairs).filter(([p]) => !p.split("|").every((k) => set.has(k)))),
    }));
  };

  const card = (e: NameEntry, other: NameEntry, ref?: { current: HTMLButtonElement | null }) => (
    <button class={`duel-card g-${genderClass(e)}`} onClick={() => pick(e.key, other.key)} ref={ref}>
      <span class={`duel-name ${nameSizeClass(e.name)}`}>{e.name}</span>
    </button>
  );

  const done = !ask && st.ranked.length >= 2;
  const a = ask ? name(ask[0]) : undefined;
  const b = ask ? name(ask[1]) : undefined;
  const winner = done ? name(st.ranked[0]) : undefined;

  return (
    <Sheet open={open} onClose={closeOverlay} title="Name-off" size="full">
      <div class="nameoff">
        {both && (
          <Segmented<"0" | "1">
            label="Which names"
            value={String(chosenGroup) as "0" | "1"}
            onChange={(v) => setGroup(Number(v) as SexIdx)}
            options={[
              { v: "0", label: `Girls (${counts[0]})` },
              { v: "1", label: `Boys (${counts[1]})` },
            ]}
          />
        )}
        {keys.length < 2 ? (
          <p class="muted">Like at least two names to start a Name-off.</p>
        ) : done && winner ? (
          <div class="nameoff-done">
            <h2>Your ranking</h2>
            <ol class="podium">
              {st.ranked.slice(0, 3).map((k, i) => {
                const e = name(k);
                return (
                  <li key={k} class={`p${i + 1} g-${genderClass(e)}`}>
                    <span class="lb-pos">{i + 1}</span>
                    <span class="lb-name">{e.name}</span>
                  </li>
                );
              })}
            </ol>
            {st.ranked.length > 3 && (
              <ol class="leaderboard-rest" start={4}>
                {st.ranked.slice(3).map((k, i) => (
                  <li key={k}>
                    <span class="lb-pos">{i + 4}</span>
                    <span>{name(k).name}</span>
                  </li>
                ))}
              </ol>
            )}
            {winner.key !== chosenKey ? (
              <div class="nameoff-choose">
                <p>{winner.name} came out on top. Is it the one?</p>
                <button class="btn btn-primary" onClick={() => pickName(winner.key, winner.name)}>
                  Choose {winner.name}
                </button>
              </div>
            ) : (
              <p class="muted">{winner.name} is your chosen name.</p>
            )}
            <button class="btn btn-ghost" onClick={again}>
              Start this Name-off again
            </button>
          </div>
        ) : (
          a &&
          b && (
            <>
              <p class="nameoff-intro">Which do you prefer? Each pick places a name in your ranking.</p>
              <div class="nameoff-progress" role="progressbar" aria-label="Names placed" aria-valuemin={0} aria-valuemax={keys.length} aria-valuenow={st.placed}>
                <span style={{ width: `${(st.placed / keys.length) * 100}%` }} />
              </div>
              <p class="muted small">
                {st.placed} of {keys.length} names placed
              </p>
              <div class="duel" key={`${ask![0]}|${ask![1]}`}>
                {card(a, b, first)}
                <span class="duel-or" aria-hidden="true">
                  or
                </span>
                {card(b, a)}
              </div>
              <div class="duel-foot">
                <button class="btn btn-ghost" onClick={skip}>
                  Can't decide
                </button>
              </div>
              {st.ranked.length > 1 && (
                <div class="leaderboard">
                  <h3>So far</h3>
                  <ol>
                    {st.ranked.slice(0, 5).map((k, i) => {
                      const e = name(k);
                      return (
                        <li key={k} class={`g-${genderClass(e)}`}>
                          <span class="lb-pos">{i + 1}</span>
                          <span class="lb-name">{e.name}</span>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </>
          )
        )}
        <p class="sr-only" aria-live="polite">
          {said && (done ? `${said} Done: ${winner?.name} came out on top.` : a && b ? `${said} Next: ${a.name} or ${b.name}.` : said)}
        </p>
      </div>
    </Sheet>
  );
}
